import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, Plus, Trash2, ChevronUp, ChevronDown, Save, Loader2, AlertTriangle } from "lucide-react";

const CATEGORIES = [{ code: "procesos", label: "Procesos" }, { code: "limpieza", label: "Limpieza" }, { code: "supervision", label: "Supervisión" }];
const RECURRENCES = [
  { code: "daily", label: "Diaria (1 run/día, sin turnos)" },
  { code: "daily_per_turn", label: "Diaria por turno" },
  { code: "weekly_by_dow", label: "Varía por día de semana" },
  { code: "on_demand", label: "Bajo demanda (solo manual)" },
];
const ITEM_TYPES = [
  { v: "check", label: "Sí/No/N.A." }, { v: "check_evidence", label: "Sí/No/N.A. + foto si Sí" },
  { v: "number", label: "Número" }, { v: "text", label: "Texto libre" },
  { v: "temperature", label: "Temperatura (°C)" }, { v: "check_dual_column", label: "Doble columna AP/CR" },
];
const DOWS = ["lunes", "martes", "miercoles", "jueves", "viernes", "sabado", "domingo"];
const ROLES = ["branch_manager", "corporate_admin", "operativo"];
const TURN_LABEL = { apertura: "Apertura", intermedio: "Producción", cierre: "Cierre" };

const emptyTemplate = {
  name: "", category_code: "procesos", description: "", recurrence: "daily_per_turn",
  turns: ["apertura"], applies_to_roles: ["branch_manager", "corporate_admin"], applies_to_branches: [],
  sections: [{ code: "apertura", name: "Apertura", order: 1, items: [] }], active: true,
};

function newItem(order) {
  return { code: `item_${Date.now()}_${order}`, label: "", type: "check", evidence_required: false, evidence_optional: false, force_evidence_on_no: false, max_photos: 5, order };
}

export default function OpsAdminChecklistEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "nueva";
  const [tpl, setTpl] = useState(emptyTemplate);
  const [activeDow, setActiveDow] = useState("lunes");
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [branches, setBranches] = useState([]);

  const load = useCallback(async () => {
    if (isNew) return;
    try {
      const res = await api.get(`/ops/checklist-templates/${id}`);
      setTpl(res.data);
    } catch {
      toast.error("No se pudo cargar la plantilla");
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/branches").then((res) => setBranches(res.data.filter((b) => !b.is_corporate))).catch(() => setBranches([]));
  }, []);

  const isWeekly = tpl.recurrence === "weekly_by_dow";

  const getSections = () => (isWeekly ? (tpl.sections_by_dow?.[activeDow] || []) : (tpl.sections || []));
  const setSections = (secs) => {
    if (isWeekly) setTpl((p) => ({ ...p, sections_by_dow: { ...(p.sections_by_dow || {}), [activeDow]: secs } }));
    else setTpl((p) => ({ ...p, sections: secs }));
  };

  const addSection = () => setSections([...getSections(), { code: `seccion_${Date.now()}`, name: "Nueva sección", order: getSections().length + 1, items: [] }]);
  const removeSection = (i) => setSections(getSections().filter((_, idx) => idx !== i));
  const updateSectionName = (i, name) => setSections(getSections().map((s, idx) => (idx === i ? { ...s, name } : s)));

  const addItem = (si) => setSections(getSections().map((s, idx) => (idx === si ? { ...s, items: [...s.items, newItem(s.items.length + 1)] } : s)));
  const removeItem = (si, ii) => setSections(getSections().map((s, idx) => (idx === si ? { ...s, items: s.items.filter((_, j) => j !== ii) } : s)));
  const updateItem = (si, ii, patch) => setSections(getSections().map((s, idx) => (idx === si ? { ...s, items: s.items.map((it, j) => (j === ii ? { ...it, ...patch } : it)) } : s)));
  const moveItem = (si, ii, dir) => {
    setSections(getSections().map((s, idx) => {
      if (idx !== si) return s;
      const items = [...s.items];
      const target = ii + dir;
      if (target < 0 || target >= items.length) return s;
      [items[ii], items[target]] = [items[target], items[ii]];
      return { ...s, items };
    }));
  };

  const toggleTurn = (turn) => setTpl((p) => ({ ...p, turns: p.turns.includes(turn) ? p.turns.filter((t) => t !== turn) : [...p.turns, turn] }));
  const toggleRole = (role) => setTpl((p) => ({ ...p, applies_to_roles: p.applies_to_roles.includes(role) ? p.applies_to_roles.filter((r) => r !== role) : [...p.applies_to_roles, role] }));
  const toggleBranch = (branchId) => setTpl((p) => {
    const cur = p.applies_to_branches || [];
    return { ...p, applies_to_branches: cur.includes(branchId) ? cur.filter((b) => b !== branchId) : [...cur, branchId] };
  });

  const handleSave = async () => {
    if (!tpl.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      if (isNew) {
        const res = await api.post("/ops/checklist-templates", tpl);
        toast.success("Plantilla creada");
        navigate(`/ops/admin/checklists/${res.data.id}`);
      } else {
        await api.put(`/ops/checklist-templates/${id}`, tpl);
        toast.success("Plantilla actualizada");
        load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-5 pb-20" data-testid="checklist-editor-page">
      <button onClick={() => navigate("/ops/admin/checklists")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
        <ChevronLeft className="w-4 h-4" /> Volver
      </button>

      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-500">Nombre</label>
            <input value={tpl.name} onChange={(e) => setTpl((p) => ({ ...p, name: e.target.value }))} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm mt-1" data-testid="editor-name-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Categoría</label>
            <select value={tpl.category_code} onChange={(e) => setTpl((p) => ({ ...p, category_code: e.target.value }))} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm mt-1" data-testid="editor-category-select">
              {CATEGORIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">Descripción</label>
          <input value={tpl.description} onChange={(e) => setTpl((p) => ({ ...p, description: e.target.value }))} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm mt-1" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-500">Recurrencia</label>
            <select value={tpl.recurrence} onChange={(e) => setTpl((p) => ({ ...p, recurrence: e.target.value }))} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm mt-1" data-testid="editor-recurrence-select">
              {RECURRENCES.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
            </select>
          </div>
          {(tpl.recurrence === "daily_per_turn" || tpl.recurrence === "weekly_by_dow") && (
            <div>
              <label className="text-xs font-semibold text-slate-500">Turnos</label>
              <div className="flex gap-1.5 mt-1.5">
                {["apertura", "intermedio", "cierre"].map((t) => (
                  <button key={t} onClick={() => toggleTurn(t)} className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${tpl.turns.includes(t) ? "bg-[#FFDF00] border-[#FFDF00]" : "border-slate-200 text-slate-500"}`}>{TURN_LABEL[t]}</button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">Aplica a roles</label>
          <div className="flex gap-1.5 mt-1.5">
            {ROLES.map((r) => (
              <button key={r} onClick={() => toggleRole(r)} className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${tpl.applies_to_roles.includes(r) ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-500"}`}>{r}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">Aplica a sucursales</label>
          <p className="text-[11px] text-slate-400 mb-1.5">Sin seleccionar ninguna = aplica a todas las sucursales.</p>
          <div className="flex gap-1.5 flex-wrap" data-testid="editor-branches-selector">
            {branches.map((b) => (
              <button
                key={b.id} onClick={() => toggleBranch(b.id)} data-testid={`editor-branch-${b.id}`}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${(tpl.applies_to_branches || []).includes(b.id) ? "bg-emerald-600 text-white border-emerald-600" : "border-slate-200 text-slate-500"}`}
              >
                {b.name}
              </button>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={tpl.active} onChange={(e) => setTpl((p) => ({ ...p, active: e.target.checked }))} /> Activa
        </label>
      </div>

      {isWeekly && (
        <div className="flex gap-1 flex-wrap">
          {DOWS.map((d) => (
            <button key={d} onClick={() => setActiveDow(d)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize ${activeDow === d ? "bg-[#FFDF00]" : "bg-slate-100 text-slate-500"}`}>{d}</button>
          ))}
        </div>
      )}

      <div className="space-y-3">
        {getSections().map((sec, si) => (
          <div key={sec.code} className="bg-white rounded-xl border border-slate-200 p-4 space-y-2">
            <div className="flex items-center gap-2">
              <input value={sec.name} onChange={(e) => updateSectionName(si, e.target.value)} className="flex-1 font-semibold text-sm border-b border-transparent hover:border-slate-200 focus:border-slate-300 focus:outline-none px-1 py-0.5" />
              <button onClick={() => removeSection(si)} className="p-1 text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="space-y-2">
              {sec.items.map((item, ii) => (
                <div key={item.code} className="border border-slate-100 rounded-lg p-2.5 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <input
                      value={item.label} onChange={(e) => updateItem(si, ii, { label: e.target.value })}
                      placeholder="Texto del ítem..." className="flex-1 border border-slate-200 rounded-lg px-2 py-1.5 text-sm"
                      data-testid={`editor-item-label-${si}-${ii}`}
                    />
                    {item.needs_validation && <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0" title="Necesita revisión" />}
                    <button onClick={() => moveItem(si, ii, -1)} disabled={ii === 0} className="p-1 disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" /></button>
                    <button onClick={() => moveItem(si, ii, 1)} disabled={ii === sec.items.length - 1} className="p-1 disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
                    <button onClick={() => removeItem(si, ii)} className="p-1 text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap pl-1">
                    <select value={item.type} onChange={(e) => updateItem(si, ii, { type: e.target.value, columns: e.target.value === "check_dual_column" ? [{ code: "AP", label: "Apertura" }, { code: "CR", label: "Cierre" }] : undefined })} className="border border-slate-200 rounded-lg px-2 py-1 text-xs">
                      {ITEM_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                    </select>
                    {item.type !== "check_dual_column" && (
                      <>
                        <label className="flex items-center gap-1 text-xs text-slate-600"><input type="checkbox" checked={!!item.evidence_optional} onChange={(e) => updateItem(si, ii, { evidence_optional: e.target.checked })} /> Foto opcional</label>
                        <label className="flex items-center gap-1 text-xs text-slate-600"><input type="checkbox" checked={item.type === "check_evidence"} onChange={(e) => updateItem(si, ii, { type: e.target.checked ? "check_evidence" : "check", evidence_required: e.target.checked })} /> Foto obligatoria si Sí</label>
                        {(item.evidence_optional || item.evidence_required) && (
                          <label className="flex items-center gap-1 text-xs text-slate-600">
                            Máx. fotos
                            <input
                              type="number" min={1} max={20} value={item.max_photos ?? 5}
                              onChange={(e) => updateItem(si, ii, { max_photos: parseInt(e.target.value, 10) || 5 })}
                              className="w-12 border border-slate-200 rounded-lg px-1.5 py-0.5 text-xs"
                              data-testid={`editor-item-maxphotos-${si}-${ii}`}
                            />
                          </label>
                        )}
                      </>
                    )}
                    {(item.type === "check_dual_column") && (
                      <label className="flex items-center gap-1 text-xs text-slate-600"><input type="checkbox" checked={!!item.force_evidence_on_no} onChange={(e) => updateItem(si, ii, { force_evidence_on_no: e.target.checked })} /> Foto+nota obligatoria si No</label>
                    )}
                    {item.type === "temperature" && (
                      <>
                        <input type="number" placeholder="min" value={item.range_min ?? ""} onChange={(e) => updateItem(si, ii, { range_min: e.target.value === "" ? null : parseFloat(e.target.value) })} className="w-16 border border-slate-200 rounded-lg px-2 py-1 text-xs" />
                        <span className="text-xs text-slate-400">a</span>
                        <input type="number" placeholder="max" value={item.range_max ?? ""} onChange={(e) => updateItem(si, ii, { range_max: e.target.value === "" ? null : parseFloat(e.target.value) })} className="w-16 border border-slate-200 rounded-lg px-2 py-1 text-xs" />
                        <span className="text-xs text-slate-400">°C</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => addItem(si)} className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800 px-1 py-1"><Plus className="w-3.5 h-3.5" /> Agregar ítem</button>
          </div>
        ))}
        <button onClick={addSection} className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-dashed border-slate-300 text-sm font-medium text-slate-500 hover:bg-slate-50 w-full justify-center" data-testid="editor-add-section-btn">
          <Plus className="w-4 h-4" /> Agregar sección {isWeekly ? `(${activeDow})` : ""}
        </button>
      </div>

      <div className="fixed bottom-6 right-24">
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-5 py-3 rounded-xl bg-slate-900 text-white font-bold shadow-lg hover:bg-slate-800 disabled:opacity-60" data-testid="editor-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
        </button>
      </div>
    </div>
  );
}
